import {
  OperationLayerMap,
  resolveTopologicalPathForBlock,
} from '@festina-life/api'
import { notEmpty, type Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'
import { useParams } from 'react-router'

import type { FilterStatus } from '../../components/configurationItemsTree/types'
import { useMetaModelContext } from '../../context/metaModelContext'
import { useTemplateIsLibrary } from '../../hooks/useTemplateIsLibrary'
import { useTranslateLayer } from '../../hooks/useTranslateLayer'
import type { AnyConfigurationRouteParams } from '../../system/types'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useAllLayersOnServer } from '../../uiModels/api/metaModel/useAllLayersOnServer'
import type { IndexedParameterDefinitionCreateOperations } from '../../uiModels/parameterOperations/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import {
  createLayerConfigurationItemsTree,
  filterEnumDefinition,
  filterGlMapOperation,
  filterParameterOperation,
  filterReconciliationSpecOperation,
  filterRegisteredTenant,
  filterTemplateOperation,
} from './helpers'

export const useConfigurationItemsTree = ({
  filterStatus,
  shouldShowEmptyNodes,
  shouldIncludeLayers,
}: {
  filterStatus?: Optional<FilterStatus>
  shouldShowEmptyNodes?: Optional<boolean>
  shouldIncludeLayers?: Optional<boolean>
} = {}) => {
  const {
    templateOperations,
    parameterDefinitionCreateOperations,
    parameterValueEntityOperations,
    registeredTenantCreateOperations,
    glMapOperations,
    reconciliationSpecCreateOperations,
    configuredEnums,
  } = useOperationsContext()
  const translate = useTranslate()
  const {
    parameterDefinitionId,
    templateId,
    enumId,
    registeredTenantId,
    mappingId,
    specId,
  } = useParams<AnyConfigurationRouteParams>()
  const templateIsLibrary = useTemplateIsLibrary()
  const {
    server,
    layer: activeLayer,
    block: activeBlock,
  } = useNavigationParams()
  const translateLayer = useTranslateLayer()
  const { layerTopology, blockTopology } = useMetaModelContext()
  const allBlocksAndLayers = useAllLayersOnServer({
    server,
    allTenants: registeredTenantCreateOperations,
  })

  return useMemo(() => {
    const blockFilter = filterStatus?.blockFilter
    const layerFilter = filterStatus?.layerFilter
    const visibleBlocks = new Set<string>()

    if (blockFilter?.mode === 'single' && blockFilter.selectedBlock) {
      visibleBlocks.add(blockFilter.selectedBlock)
    } else if (
      blockFilter?.mode === 'withBaseBlocks' &&
      blockFilter.selectedBlock
    ) {
      resolveTopologicalPathForBlock(
        blockFilter.selectedBlock,
        blockTopology,
      ).forEach((block) => visibleBlocks.add(block))
    }

    const allBlockItems = Array.from(allBlocksAndLayers.entries())
      .filter(([block]) => !visibleBlocks.size || visibleBlocks.has(block))
      .map(([block, layersForBlock]) => {
        const blockLayerItems = new OperationLayerMap(
          layersForBlock
            .filter(
              (blockLayer) => !layerFilter || blockLayer.equals(layerFilter),
            )
            .map((blockLayer) => {
              const visibleTemplates = Array.from(
                templateOperations.get(block)?.get(blockLayer)?.values() ?? [],
              ).filter((indexedTemplate) =>
                filterTemplateOperation({
                  indexedTemplate,
                  isLibrary: templateIsLibrary(indexedTemplate.templateId),
                  filterStatus,
                }),
              )

              const visibleParameterDefinitions = Array.from(
                parameterDefinitionCreateOperations
                  .get(block)
                  ?.get(blockLayer)
                  ?.values() ?? [],
              ).filter((indexedParameterDefinition) =>
                filterParameterOperation({
                  operations: indexedParameterDefinition.operations,
                  filterStatus,
                }),
              )
              const visibleParameterValues = Array.from(
                parameterValueEntityOperations
                  .get(block)
                  ?.get(blockLayer)
                  ?.entries() ?? [],
              )
                .filter(([, indexedParameterValue]) =>
                  filterParameterOperation({
                    operations: indexedParameterValue,
                    filterStatus,
                  }),
                )
                .map(([parameterId]) => parameterId)

              const parameterKeys = Array.from(
                new Set(
                  visibleParameterDefinitions
                    ?.map(
                      (parameterDefinition) => parameterDefinition.parameterId,
                    )
                    .concat(visibleParameterValues ?? []),
                ),
              )

              const lastOrDraftLiteral = Array.from(
                configuredEnums.get(block)?.get(blockLayer)?.values() ?? [],
              ).filter((enumDefinition) =>
                filterEnumDefinition({
                  definition: enumDefinition,
                  filterStatus,
                }),
              )

              const visibleRegisteredTenants = Array.from(
                registeredTenantCreateOperations
                  .get(block)
                  ?.get(blockLayer)
                  ?.values() ?? [],
              )
                .flatMap((registeredTenant) => registeredTenant.operations)
                .filter((registeredTenant) =>
                  filterRegisteredTenant({
                    registeredTenant,
                    filterStatus,
                  }),
                )

              const visibleGlMaps = Array.from(
                glMapOperations.get(block)?.get(blockLayer)?.values() ?? [],
              ).filter((indexedGlMap) =>
                filterGlMapOperation({ indexedGlMap, filterStatus }),
              )

              const visibleReconciliationSpecs = Array.from(
                reconciliationSpecCreateOperations
                  .get(block)
                  ?.get(blockLayer)
                  ?.values() ?? [],
              ).filter((indexedReconciliationSpec) =>
                filterReconciliationSpecOperation({
                  indexedReconciliationSpec,
                  filterStatus,
                }),
              )

              const layerItems = [
                ...parameterKeys.map((parameterKey) => {
                  const definitions = parameterDefinitionCreateOperations
                    .get(block)
                    ?.get(blockLayer)
                    ?.get(parameterKey)

                  return {
                    definitions:
                      definitions ??
                      ({
                        operations: [],
                        parameterId: parameterKey,
                        isDraft: false,
                        lastParameterEntity: undefined,
                        packageName: undefined,
                      } satisfies IndexedParameterDefinitionCreateOperations),
                    values:
                      parameterValueEntityOperations
                        .get(block)
                        ?.get(blockLayer)
                        ?.get(parameterKey) ?? [],
                  }
                }),
                ...visibleTemplates,
                ...lastOrDraftLiteral,
                ...visibleRegisteredTenants,
                ...visibleGlMaps,
                ...visibleReconciliationSpecs,
              ]

              return layerItems.length || shouldShowEmptyNodes
                ? ([blockLayer, layerItems] as const)
                : undefined
            })
            .filter(notEmpty),
        )

        return blockLayerItems.size > 0 || shouldShowEmptyNodes
          ? { block, items: blockLayerItems }
          : undefined
      })
      .filter(notEmpty)

    return createLayerConfigurationItemsTree({
      allBlockItems,
      fallbackPackageName: translate('othersTree.fallbackPackage.other'),
      parametersPackageName: translate(
        'parametersTree.fallbackPackage.parameters',
      ),
      enumsPackageName: translate('enumsTree.fallbackPackage.enums'),
      tenantsPackageName: translate('tenantsTree.fallbackPackage.tenants'),
      glMapsPackageName: translate('glMapsTree.fallbackPackage.glMaps'),
      reconciliationSpecsPackageName: translate(
        'reconciliationSpecsTree.fallbackPackage.reconciliationSpecs',
      ),
      templateId: templateId
        ? { itemId: templateId, layer: activeLayer, block: activeBlock }
        : undefined,
      parameterDefinitionId: parameterDefinitionId
        ? {
            itemId: parameterDefinitionId,
            layer: activeLayer,
            block: activeBlock,
          }
        : undefined,
      enumId: enumId
        ? { itemId: enumId, layer: activeLayer, block: activeBlock }
        : undefined,
      registeredTenantId: registeredTenantId
        ? {
            itemId: registeredTenantId,
            layer: activeLayer,
            block: activeBlock,
          }
        : undefined,
      mappingId: mappingId
        ? {
            itemId: mappingId,
            layer: activeLayer,
            block: activeBlock,
          }
        : undefined,
      specId: specId
        ? {
            itemId: specId,
            layer: activeLayer,
            block: activeBlock,
          }
        : undefined,
      translateLayer,
      layerTopology,
      shouldIncludeLayers,
      shouldShowEmptyNodes,
    })
  }, [
    filterStatus,
    configuredEnums,
    activeLayer,
    allBlocksAndLayers,
    registeredTenantId,
    parameterDefinitionId,
    templateOperations,
    parameterDefinitionCreateOperations,
    templateId,
    enumId,
    parameterValueEntityOperations,
    templateIsLibrary,
    translate,
    registeredTenantCreateOperations,
    glMapOperations,
    mappingId,
    reconciliationSpecCreateOperations,
    specId,
    translateLayer,
    shouldShowEmptyNodes,
    activeBlock,
    layerTopology,
    blockTopology,
    shouldIncludeLayers,
  ])
}
